import { defineStore, } from "pinia";
import _ from "lodash";
import {
    FetchState,
    HsabEnrollmentSettingsResponse,
    HsabEnrollmentSettingsUpdateRequest,
    HsabEnrollmentState,
    HsabExpenseReimbursementMethod,
    HsabReimbursementMethod,
    InsuranceClaimSettings,
    PlanEnrollmentDetails,
    PlanYearEnrollmentDetails
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as userService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import * as datadog from "@/shared/datadog";
import { useNotifierStore } from "@/shared/store/notifier";
import { errorIsStatus } from "@/shared/errorHelper";

const initialState: HsabEnrollmentState = {
    employerSettingsFetchState: FetchState.Uninitialized,
    planYearEnrollmentDetailsFetchState: FetchState.Uninitialized,
    employerSettings: null,
    planYearEnrollmentDetailsResponse: null,
};

export const useHsabEnrollmentStore = defineStore("hsabEnrollment", {
    state: (): HsabEnrollmentState => (_.cloneDeep(initialState)),
    getters: {
        isEmployerSettingsLoaded (): boolean {
            return this.settingsFetchState === FetchState.Loaded;
        },
        isEmployerSettingsLoading (): boolean {
            return this.settingsFetchState === FetchState.Loading;
        },
        isEmployerSettingsFetchStateFailed (): boolean {
            return this.settingsFetchState === FetchState.Failed;
        },
        isPlanYearEnrollmentDataLoaded (): boolean {
            return this.planYearEnrollmentDetailsFetchState === FetchState.Loaded;
        },
        isPlanYearEnrollmentDataLoading (): boolean {
            return this.planYearEnrollmentDetailsFetchState === FetchState.Loading;
        },
        isPlanYearEnrollmentDataFetchStateFailed (): boolean {
            return this.planYearEnrollmentDetailsFetchState === FetchState.Failed;
        },
        insuranceClaimSettings (): InsuranceClaimSettings {
            if (this.planYearEnrollmentDetailsResponse === null) {
                return null;
            }
            return this.planYearEnrollmentDetailsResponse.insuranceClaimSettings;
        },
        userCanManageCarrierClaims (): boolean {
            const hsabAccountsStore = useHsabAccountsStore();
            const planTypesAndSubtypes = hsabAccountsStore.allActivePlanTypesAndSubTypes;
            const settingsFailed = (!this.employerSettings && this.settingsFetchState === FetchState.Failed);
            const settingsLoaded = this.employerSettings !== null;

            if (!settingsLoaded || settingsFailed) {
                return false;
            }

            const userEligible = this.employerSettings.allowOptInOutAutoClaims || this.employerSettings.enableAutomaticallyFiled;

            return (settingsLoaded && userEligible && planTypesAndSubtypes && planTypesAndSubtypes.allowHealthcareClaims === true);
        },
        reimbursementMethod (): HsabExpenseReimbursementMethod {
          const bankAccountStore = useBankAccountStore();
          if (bankAccountStore.hasDirectDeposit === false) {
              return HsabExpenseReimbursementMethod.Check;
          }
          return this.userHasNonDirectDepositReimbursementMethod ? HsabExpenseReimbursementMethod.None : HsabExpenseReimbursementMethod.DirectDeposit;
        },
        userHasDirectDepositReimbursementMethod (): boolean {
            if (this.isPlanYearEnrollmentDataLoaded) {
                return _.some(this.planYearEnrollmentDetailsResponse.planYearEnrollmentDetails,
                    x => x.reimbursementMethod === HsabExpenseReimbursementMethod.DirectDeposit);
            }
            return false;
        },
        userHasNonDirectDepositReimbursementMethod (): boolean {
            if (this.isPlanYearEnrollmentDataLoaded) {
                return _.some(this.planYearEnrollmentDetailsResponse.planYearEnrollmentDetails,
                    x => x.reimbursementMethod !== HsabExpenseReimbursementMethod.DirectDeposit);
            }
            return false;
        },
        nonDirectDepositPlans (): PlanEnrollmentDetails[] {
            if (this.isPlanYearEnrollmentDataLoaded) {
                const planYears = _.filter(this.planYearEnrollmentDetailsResponse.planYearEnrollmentDetails,
                    x => x.reimbursementMethod !== HsabExpenseReimbursementMethod.DirectDeposit);
                return _.flatten(planYears.map(x => x.planEnrollmentDetails));
            }
            return [];
        }
    },
    actions: {
        setHsabEnrollmentSettingsData (data: HsabEnrollmentSettingsResponse) {
            this.employerSettings = data;
        },
        reload () {
          this.settingsFetchState = FetchState.Loading;
            this.loadHsabEnrollmentEmployerSettings();
            this.loadPlanYearEnrollmentDetails(true);
        },
        async loadHsabEnrollmentEmployerSettings () {
            if (this.settingsFetchState === FetchState.Loaded) {
                return Promise.resolve();
            }
            return userService.getHsabEnrollmentSettings()
                .then(r => {
                    this.setHsabEnrollmentSettingsData(r.data);
                    this.settingsFetchState = FetchState.Loaded;
                })
                .catch(e => {
                    datadog.log(_.get(e, "error.message"), _.get(e, "error", {}), "error");
                    this.settingsFetchState = FetchState.Failed;
                });
        },
        async loadPlanYearEnrollmentDetails (force = false) {
            const isInAbortState = this.planYearEnrollmentDetailsFetchState === FetchState.Loaded
                || this.planYearEnrollmentDetailsFetchState === FetchState.Loading;
            if (isInAbortState && !force) {
                return Promise.resolve();
            }

            this.planYearEnrollmentDetailsFetchState = FetchState.Loading;
            return bankService.GetPlanYearEnrollmentDetails()
                .then(response => {
                    this.planYearEnrollmentDetailsResponse = response.data;
                    this.planYearEnrollmentDetailsFetchState = FetchState.Loaded;
                })
                .catch(e => {
                    this.planYearEnrollmentDetailsFetchState = FetchState.Failed;

                    if (!errorIsStatus(e, 404)) {
                        storeHelper.handleError({ error: e, rethrow: false });
                    }
                });
        },
        async saveHsabEnrollmentSettings (settings: HsabEnrollmentSettingsUpdateRequest) {
            return bankService.updateHsabEnrollmentSettings(settings)
                .then(response => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Insurance claims settings have been updated", 2000);
                })
                .catch(e => storeHelper.handleError({ error: e, rethrow: false }));
        },
        async updateNonDirectDepositEnrollmentsToDirectDeposit () {
            const requests: Promise<any>[] = [];

            const planYears: PlanYearEnrollmentDetails[] = _.filter(
                this.planYearEnrollmentDetailsResponse.planYearEnrollmentDetails,
                x => x.reimbursementMethod !== HsabReimbursementMethod.DirectDeposit);

            _.each(planYears, planYear => {
                const request = bankService.SetPlanYearEnrollmentReimbursementMethod({
                    PlanYearName: planYear.planYearName,
                    PlanYearStartDate: planYear.planYearStartDate,
                    ReimbursementMethod: HsabReimbursementMethod.DirectDeposit,
                    PlanYearHasDebitCard: planYear.isDebitCard,
                });
                requests.push(request);
            });

            return Promise.all(requests).catch(e => storeHelper.handleError({ error: e, rethrow: true, timeout: 5000 }));

        },
    }
});